import {Typography, css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  IconHeaderWithSearch,
  utils,
  omitNullsy,
  enumContainer,
  useApiQuery,
  LightSplitLayout,
  getScrollOffsetBottom,
  LoadingSpinner,
  useLocationQuery,
  ENABLE_PH_REC,
  RISKINESS_COLORS,
  DlMuiDynamicIcon,
  DateUtils,
  MaximizeButton,
  useMaximizeWrapper,
} from '@wallaby-ui/core';
import {
  DateFormatter,
  HospitalizationsLinkFormatter,
  HospitalizationsListDepartmentFormatter,
  JustTooltipFormatter,
  LightDateRangeFilter,
  LightNumberRangeFilter,
  LightSelectFilter,
  LightSelectedFilters,
  LightTextFilter,
  RiskFactorsFormatter,
  TranslateEnumFormatter,
  AG_GRID_COMPATIBILITY,
  useTableData,
  MaterialTableWrapper,
  DEFAULT_PAGE_SIZE,
  getDefaultMTOptions,
  useFormatters,
  getMultiFilter,
  useTableParams,
  MerieSchema,
  clampPageNumber,
  usePermissions,
  TableSorting,
  TablePaging,
  UserFilterDto,
  useUserFilters,
  Formatters,
  LightMedicationAtcFilter2,
  LightOrganizationFilter2,
  ChipsFormatter,
  getFollowUpDateIconTooltip,
  useInactiveAtcRenderProps,
  LightSelectedFiltersPane,
  LightFilterPanel,
  LightFilterTabDef,
  SelectedFilter,
  countSelectedFilters,
  LightSelectFilterVirtualized,
  AlertFlaggedRow,
  getAlertRowBellColumn,
  withAlertRowClass,
} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import {isEqual, sortBy} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useMemo, useRef} from 'react';
import {Helmet} from 'react-helmet';
import {defineMessages, useIntl} from 'react-intl';

import {
  applyBiochemistryRowChange,
  BIOCHEMISTRY_ROWS,
  BiochemistryRow,
  biochemistryParams,
  countBiochemistryItems,
  hasBlockingBiochemistryRow,
  isBiochemistryRowStarted,
  packBiochemistryRows,
  removeBiochemistryRow,
  showBiochemistryRows,
} from './hospitalizedPatientsBiochemistry';
import {DEFAULT_PATIENTS_FILTERS, PatientsFilters, withOccurrenceDefaults} from './hospitalizedPatientsFilters';
import {
  ACTIVE_INGREDIENT_SLOT_LABELS,
  ACTIVE_INGREDIENT_SLOT_NAMES,
  ATC_SLOT_LABELS,
  ATC_SLOT_NAMES,
  countMedicationConditions,
  MedicationSlotName,
  packMedicationSlots,
} from './hospitalizedPatientsMedication';
import {
  applyMicrobiologyRowChange,
  countMicrobiologyConditions,
  isMicrobiologyRowStarted,
  MICROBIOLOGY_ROWS,
  microbiologyParams,
  MicrobiologyPart,
  MicrobiologyRow,
  removeMicrobiologyRow,
  showMicrobiologyRows,
} from './hospitalizedPatientsMicrobiology';
import {AGE_RANGE, BMI_RANGE, WEIGHT_RANGE, otherParams} from './hospitalizedPatientsOther';
import {
  buildHospitalizedPatientsSelectedFilters,
  hospitalizedPatientsTableFiltersForChips,
  useHospitalizedPatientsChipLookups,
} from './hospitalizedPatientsSelectedFilters';
import {useHospitalizedPatientsExport} from './useHospitalizedPatientsExport';
import {useAppSelector} from 'src/js/store_hooks';

const I18N = defineMessages({
  CAPTION: {
    id: 'patient.list.caption',
    defaultMessage: 'Hospitalizations',
    description: 'caption for hospitalizations list component',
  },
  PAGE_TITLE: {
    id: 'patient.list.header.title',
    defaultMessage: 'Hospitalized patients',
    description: 'title for hospitalization list page',
  },
});
const PATIENTS_LAYOUT_SPLIT_CONFIG = [
  {
    sizes: [233, 767],
    minSize: [64, 100],
    layoutId: '/LightSplitLayout.HospitalizedPatientsLayout',
  },
];
type Props = {
  config: ConfigObjectType;
};

/**
 * Filter-panel tabs (PDM-2750): General, Medication, Biochemistry, Microbiology (PDM-2748) and Other (PDM-2749). A
 * tab can still be defined but rendered with
 * display: none (`hidden` on the tab definition), so switching it on is one flag once the filters arrive.
 */
export type HospitalizationFilterTab = 'general' | 'medication' | 'laboratory' | 'microbiology' | 'other';
type FiltersData = {
  patientsOpen: boolean;
  /** Selected filter-panel tab; part of the page state so it survives a reload like the open flag does. */
  patientsTab: HospitalizationFilterTab;
  patients: PatientsFilters;
  patientsSorting: TableSorting;
  patientsPaging: TablePaging;
};
export type PatientFiltersData = FiltersData['patients'];
const DEFAULT_PATIENTS_OPEN = true;
const DEFAULT_PATIENTS_TAB: HospitalizationFilterTab = 'general';
const DEFAULT_PATIENTS_SORTING: TableSorting = {
  field: 'RISK_SCORE' as string,
  direction: 'desc',
};
const DEFAULT_PATIENTS_PAGING: TablePaging = {};
/** The operator and the value input of a biochemistry row share this width; a value is a short number. */
const BIOCHEMISTRY_VALUE_WIDTH = 128;
/**
 * A biochemistry row must not wrap on a normal screen: lab method (362) + operator + the value field, whose text
 * filter wrapper keeps the standard 176px around the narrower input, the 8px gaps between them, the 2px gap and the
 * 18px remove cross after the value, and the 8px padding on both sides of the box. The filter panel keeps this much
 * for its inputs before the saved-filters column starts to give way.
 */
const BIOCHEMISTRY_ROW_WIDTH = 362 + 8 + BIOCHEMISTRY_VALUE_WIDTH + 8 + 176 + 2 + 18 + 2 * 8;
/**
 * The value field of a biochemistry row is a 128px input inside the standard 176px text-filter wrapper, so its remove
 * cross ends up this far from the input. The microbiology rows have no such wrapper and add the gap themselves, so
 * that the cross sits at the same distance from the last field in both boxes.
 */
const CONDITION_ROW_REMOVE_GAP = 176 - BIOCHEMISTRY_VALUE_WIDTH;

/**
 * The occurrence selects (medication, biochemistry values) are as wide as the date filters next to them (176px, see
 * FilterNodeTypeDateRange). A select without an explicit width keeps the 362px of a full-width filter.
 */
const MATCH_FILTER_WIDTH = 176;

export const HospitalizedPatientsLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'hospitalizationList';
  const storageIdPrefix = '/hospitalizedPatients';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(() => {
    return {
      patientsOpen: query.patientsOpen ?? DEFAULT_PATIENTS_OPEN,
      patientsTab: query.patientsTab ?? DEFAULT_PATIENTS_TAB,
      patients: withOccurrenceDefaults(query?.patients ?? DEFAULT_PATIENTS_FILTERS),
      patientsSorting: query?.patientsSorting ?? DEFAULT_PATIENTS_SORTING,
      patientsPaging: query?.patientsPaging ?? DEFAULT_PATIENTS_PAGING,
    };
  }, [query]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({patients: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <HospitalizedPatientsLayoutInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
};
/** The list row; `alerts` is the row bell (PDW-240), optional until the generated schema carries it. */
type RowType = MerieSchema.components['schemas']['EvaluatedEncounterDto'] & AlertFlaggedRow;

/**
 * @param showAlertBell whether the user holds the Use alerts right - the leading bell column exists only then
 * (the BE sends an empty `alerts` to everyone else, so the response can never decide it)
 */
function getColumns(formatters: Formatters, defaultSort: TableSorting, showAlertBell: boolean) {
  const columns = ENABLE_PH_REC ? getNewColumns(formatters, defaultSort) : getOldColumns(formatters, defaultSort);
  return showAlertBell ? [getAlertRowBellColumn<RowType>(), ...columns] : columns;
}
// NOTE: old columns for next release
function getOldColumns(formatters: Formatters, defaultSort: TableSorting): Column<RowType>[] {
  const {t, tEnum, formatDateShort} = formatters;
  const columns: Column<RowType>[] = [
    {
      field: 'patientFullName',
      title: t('patient.list.grid.fullName'),
      tooltip: t('patient.list.grid.fullName.tooltip'),
      render: (data) => (
        <HospitalizationsLinkFormatter {...AG_GRID_COMPATIBILITY} value={data.patient?.fullName} data={data} versionSuffix='/v2' />
      ),
      width: '165px',
    },
    {
      field: 'patient.identificationNumber',
      title: t('patient.list.grid.identificationNumber'),
      tooltip: t('patient.list.grid.identificationNumber.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data.patient?.identificationNumber} />,
      sorting: false,
    },
    {
      field: 'encounterOrganization',
      title: t('patient.list.grid.admissionDepartment'),
      tooltip: t('patient.list.grid.admissionDepartment.tooltip'),
      render: (data) => <HospitalizationsListDepartmentFormatter value={data.encounterOrganization} data={data} />,
      sorting: false,
    },
    {
      field: 'ENCOUNTER_STARTED_DATE',
      title: t('patient.list.grid.admissionDate'),
      tooltip: t('patient.list.grid.admissionDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.encounterStartedAt} />,
      customSort: () => 0,
    },
    {
      field: 'lastOrganization',
      title: t('patient.list.grid.currentDepartment'),
      tooltip: t('patient.list.grid.currentDepartment.tooltip'),
      render: (data) => <HospitalizationsListDepartmentFormatter value={data.lastOrganization} data={data} />,
      sorting: false,
    },
    {
      field: 'LAST_ORGANIZATION_ADMISSION_DATE',
      title: t('patient.list.grid.transferDate'),
      tooltip: t('patient.list.grid.transferDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.lastOrganizationAdmissionDate} />,
      customSort: () => 0,
    },
    {
      field: 'RISK_SCORE',
      title: t('patient.list.grid.riskScore'),
      tooltip: t('patient.list.grid.riskScore.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data.riskScoreValue} />,
    },
    {
      field: 'riskFactorsCount',
      title: t('patient.list.grid.riskFactors'),
      tooltip: t('patient.list.grid.riskFactors.tooltip'),
      render: (data) => <RiskFactorsFormatter value={data.activeRiskFactorsCount ?? data.riskFactorsCount} data={data} />, // NOTE: BE version agnostic
    },
    {
      field: 'RISK_SEVERITY',
      title: t('patient.list.grid.riskSeverity'),
      tooltip: t('patient.list.grid.riskSeverity.tooltip'),
      render: (data) => (
        <TranslateEnumFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data.riskSeverity}
          colDef={{cellRendererParams: {enumName: 'RiskSeverityEnum'}}}
        />
      ),
    },
    {
      field: 'EVALUATION_STATE_MODIFIED_DATE',
      title: t('patient.list.grid.checkState'),
      tooltip: t('patient.list.grid.checkState.tooltip'),
      render: (data) => {
        const checkState = data.evaluationState ? tEnum('EvaluationStateEnum', data.evaluationState) : t('global.unknown');
        const checkDate = data.evaluationStateModifiedOn ? formatDateShort(data.evaluationStateModifiedOn) : undefined;
        return <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={[checkDate, checkState].filter((v) => v).join(' - ')} />;
      },
      customSort: () => 0,
    },
    /* {
      field: 'lastMedicationsCount',
      title: t('patient.list.grid.medications'),
      tooltip: t('patient.list.grid.medications.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data.lastMedicationsCount}
        />
      ),
    }, */
  ];
  return columns.map((v) => ({
    ...v,
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}
// NOTE: new columns for demo
function getNewColumns(formatters: Formatters, defaultSort: TableSorting): Column<RowType>[] {
  const {t, tEnum, formatDateShort} = formatters;
  const columns: Column<RowType>[] = [
    {
      field: 'patientFullName',
      title: t('patient.list.grid.fullName'),
      tooltip: t('patient.list.grid.fullName.tooltip'),
      render: (data) => {
        const texts = [data.patient?.fullName ?? ''];
        const label = texts.join(', ');

        const accTooltip = [] as React.ReactNode[];
        accTooltip.push(<span key='label'>{label}</span>);
        // NOTE: tooltip has `maxWidth: 300`
        if (data.comment) {
          accTooltip.push(<hr key='divider' className='tooltip-divider' />);
          accTooltip.push(
            <div key='comment' data-ellipsis='3'>
              {data.comment}
            </div>
          );
        }
        const tooltip = <>{accTooltip}</>;

        return <HospitalizationsLinkFormatter {...AG_GRID_COMPATIBILITY} data={data} value={label} tooltip={tooltip} versionSuffix='/v2' />;
      },
      width: '165px',
    },
    {
      field: 'patient.identificationNumber',
      title: t('patient.list.grid.identificationNumber'),
      tooltip: t('patient.list.grid.identificationNumber.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data.patient?.identificationNumber} />,
      sorting: false,
    },
    {
      field: 'encounterOrganization',
      title: t('patient.list.grid.admissionDepartment'),
      tooltip: t('patient.list.grid.admissionDepartment.tooltip'),
      render: (data) => <HospitalizationsListDepartmentFormatter value={data.encounterOrganization} data={data} />,
      sorting: false,
    },
    {
      field: 'ENCOUNTER_STARTED_DATE',
      title: t('patient.list.grid.admissionDate'),
      tooltip: t('patient.list.grid.admissionDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.encounterStartedAt} />,
      customSort: () => 0,
    },
    {
      field: 'lastOrganization',
      title: t('patient.list.grid.currentDepartment'),
      tooltip: t('patient.list.grid.currentDepartment.tooltip'),
      render: (data) => <HospitalizationsListDepartmentFormatter value={data.lastOrganization} data={data} />,
      sorting: false,
    },
    {
      field: 'LAST_ORGANIZATION_ADMISSION_DATE',
      title: t('patient.list.grid.transferDate'),
      tooltip: t('patient.list.grid.transferDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.lastOrganizationAdmissionDate} />,
      customSort: () => 0,
    },
    {
      field: 'RISK_SCORE',
      title: t('patient.list.grid.riskScore'),
      tooltip: t('patient.list.grid.riskScore.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data.riskScoreValue} />,
    },
    {
      field: 'riskFactorsCount',
      title: t('patient.list.grid.riskFactors'),
      tooltip: t('patient.list.grid.riskFactors.tooltip'),
      render: (data) => <RiskFactorsFormatter value={data.activeRiskFactorsCount ?? data.riskFactorsCount} data={data} />, // NOTE: BE version agnostic
    },
    {
      field: 'pharmaRecommendationRiskiness',
      title: t('patient.list.grid.pharmaRecommendationRiskiness'),
      tooltip: t('patient.list.grid.pharmaRecommendationRiskiness.tooltip'),
      render: (data) => {
        const {pharmaRecommendationRiskiness} = data;
        if (pharmaRecommendationRiskiness == null) return '-';
        return (
          <ChipsFormatter
            value={[pharmaRecommendationRiskiness]}
            getInfo={(riskiness) => ({
              label: tEnum('Riskiness', riskiness),
              background: RISKINESS_COLORS[riskiness]?.background,
            })}
            tooltip={tEnum('Riskiness', pharmaRecommendationRiskiness)}
          />
        );
      },
      customSort: () => 0,
    },
    {
      field: 'PHARMA_RECOMMENDATION_DATE',
      title: t('patient.list.grid.pharmaRecommendationDate'),
      tooltip: t('patient.list.grid.pharmaRecommendationDate.tooltip'),
      render: (data) => <DateFormatter {...AG_GRID_COMPATIBILITY} value={data.pharmaRecommendationDate} />,
    },
    {
      field: 'PHARMA_RECOMMENDATION_FOLLOW_UP_DATE',
      title: t('patient.list.grid.pharmaRecommendationFollowUpDate'),
      tooltip: t('patient.list.grid.pharmaRecommendationFollowUpDate.tooltip'),
      render: (data) => {
        const followUpDate = data.pharmaRecommendationFollowUpDate;
        if (followUpDate == null) return '-';

        const text = formatDateShort(followUpDate);
        const {iconName, tooltip} = getFollowUpDateIconTooltip(followUpDate, formatters);

        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={
              <div style={{display: 'inline-flex', alignItems: 'center', wordBreak: 'break-word'}}>
                {iconName && (
                  <DlMuiDynamicIcon
                    iconName={iconName}
                    useFixedViewBox='0 0 14 16'
                    noButtonStyles
                    style={{padding: 0, marginRight: 4}}
                    svgIconStyle={{fontSize: 18}}
                  />
                )}
                <span>{text}</span>
              </div>
            }
            tooltipOverride={tooltip ?? text}
          />
        );
      },
    },
    /* {
      field: 'lastMedicationsCount',
      title: t('patient.list.grid.medications'),
      tooltip: t('patient.list.grid.medications.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data.lastMedicationsCount}
        />
      ),
    }, */
  ];
  return columns.map((v) => ({
    ...v,
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}
export function normalizeHospitalizedPatientsFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  // The occurrence defaults are part of the comparison too, so a saved filter from before the keys existed still
  // matches the panel that shows it.
  const copy: Record<string, any> = withOccurrenceDefaults(structuredClone(filter ?? {}) as Partial<PatientsFilters>);
  if (copy.encounterFloatingShortcutName) {
    delete (copy as any).encounterStartedFrom;
    delete (copy as any).encounterStartedTo;
  }
  if (copy.lastEncounterSubFloatingShortcutName) {
    delete (copy as any).lastEncounterSubStartedFrom;
    delete (copy as any).lastEncounterSubStartedTo;
  }
  if (copy.pharmaRecommendationFloatingShortcutName) {
    delete (copy as any).pharmaRecommendationDateFrom;
    delete (copy as any).pharmaRecommendationDateTo;
  }
  if (copy.pharmaRecommendationFollowUpFloatingShortcutName) {
    delete (copy as any).pharmaRecommendationFollowUpDateFrom;
    delete (copy as any).pharmaRecommendationFollowUpDateTo;
  }
  if (copy.atcHierarchyFloatingShortcutName) {
    delete (copy as any).atcHierarchyFrom;
    delete (copy as any).atcHierarchyTo;
  }
  if (copy.biochemistryFloatingShortcutName) {
    delete (copy as any).biochemistryFrom;
    delete (copy as any).biochemistryTo;
  }
  if (copy.microbiologyFloatingShortcutName) {
    delete (copy as any).microbiologyFrom;
    delete (copy as any).microbiologyTo;
  }
  return copy;
}
const HospitalizedPatientsLayoutInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  // TODO: PDM-1342 fix number range inputs??
  const intl = useIntl();
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const {isLoading: permissionsLoading, userRoles} = usePermissions(config);

  const numberRangesUrl = config.service.url.numberRange;
  const {data: numberRangesData} = useApiQuery([numberRangesUrl], null, {
    axiosConfig: {
      url: numberRangesUrl,
    },
  });
  const {riskScoreRange, riskFactorsRange} = useMemo(() => {
    return {
      riskScoreRange: numberRangesData?.['EvaluatedEncounterDto.riskScoreValue'] ?? {},
      riskFactorsRange: numberRangesData?.['EvaluatedEncounterDto.riskFactorsCount'] ?? {},
      lastMedicationsRange: numberRangesData?.['EvaluatedEncounterDto.lastMedicationsCount'] ?? {},
    };
  }, [numberRangesData]);

  const hospitalizationsUrl = config.service.url.hospitalizations;
  const hospitalizationParams = useTableParams(
    {
      patientFullName: filters.patients.patientFullName,
      patientIdentificationNumber: filters.patients.patientIdentificationNumber,
      patientSrcId: filters.patients.patientSrcId,
      lastEncounterSubOrganizationIds:
        filters.patients.lastEncounterSubOrganizationIds && filters.patients.lastEncounterSubOrganizationIds.join(','),
      encounterOrganizationIds: filters.patients.encounterOrganizationIds && filters.patients.encounterOrganizationIds.join(','),
      lastEncounterSubStartedFrom: filters.patients.lastEncounterSubStartedFrom,
      lastEncounterSubStartedTo: filters.patients.lastEncounterSubStartedTo,
      lastEncounterSubFloatingShortcutName: filters.patients.lastEncounterSubFloatingShortcutName,
      encounterStartedFrom: filters.patients.encounterStartedFrom,
      encounterStartedTo: filters.patients.encounterStartedTo,
      encounterFloatingShortcutName: filters.patients.encounterFloatingShortcutName,
      hospitalizationStates: filters.patients.hospitalizationStates && filters.patients.hospitalizationStates.join(','),
      riskScoreFrom: filters.patients.riskScoreFrom,
      riskScoreTo: filters.patients.riskScoreTo,
      riskFactorTypes: filters.patients.riskFactorTypes && filters.patients.riskFactorTypes.join(','),
      riskFactorsCountFrom: filters.patients.riskFactorsCountFrom,
      riskFactorsCountTo: filters.patients.riskFactorsCountTo,
      commentSearchString: filters.patients.commentSearchString,
      // lastMedicationsCountFrom: filters.patients.lastMedicationsCount?.start,
      // lastMedicationsCountTo: filters.patients.lastMedicationsCount?.end,
      atcHierarchyIds1: filters.patients.atcHierarchyIds1 && filters.patients.atcHierarchyIds1.join(','),
      atcHierarchyIds2: filters.patients.atcHierarchyIds2 && filters.patients.atcHierarchyIds2.join(','),
      atcHierarchyIds3: filters.patients.atcHierarchyIds3 && filters.patients.atcHierarchyIds3.join(','),
      activeIngredientIds1: filters.patients.activeIngredientIds1 && filters.patients.activeIngredientIds1.join(','),
      activeIngredientIds2: filters.patients.activeIngredientIds2 && filters.patients.activeIngredientIds2.join(','),
      activeIngredientIds3: filters.patients.activeIngredientIds3 && filters.patients.activeIngredientIds3.join(','),
      atcHierarchyFrom: filters.patients.atcHierarchyFrom,
      atcHierarchyTo: filters.patients.atcHierarchyTo,
      atcHierarchyFloatingShortcutName: filters.patients.atcHierarchyFloatingShortcutName,
      atcHierarchyMatch: filters.patients.atcHierarchyMatch,
      ...biochemistryParams(filters.patients),
      biochemistryFrom: filters.patients.biochemistryFrom,
      biochemistryTo: filters.patients.biochemistryTo,
      biochemistryFloatingShortcutName: filters.patients.biochemistryFloatingShortcutName,
      biochemistryMatch: filters.patients.biochemistryMatch,
      ...microbiologyParams(filters.patients),
      microbiologyFrom: filters.patients.microbiologyFrom,
      microbiologyTo: filters.patients.microbiologyTo,
      microbiologyFloatingShortcutName: filters.patients.microbiologyFloatingShortcutName,
      microbiologyMatch: filters.patients.microbiologyMatch,
      ...otherParams(filters.patients),
      ...(ENABLE_PH_REC
        ? {
            pharmaRecommendationRiskinesses:
              filters.patients.pharmaRecommendationRiskinesses && filters.patients.pharmaRecommendationRiskinesses.join(','),
            pharmaRecommendationDateFrom: filters.patients.pharmaRecommendationDateFrom,
            pharmaRecommendationDateTo: filters.patients.pharmaRecommendationDateTo,
            pharmaRecommendationFloatingShortcutName: filters.patients.pharmaRecommendationFloatingShortcutName,
            pharmaRecommendationFollowUpDateFrom: filters.patients.pharmaRecommendationFollowUpDateFrom,
            pharmaRecommendationFollowUpDateTo: filters.patients.pharmaRecommendationFollowUpDateTo,
            pharmaRecommendationFollowUpFloatingShortcutName: filters.patients.pharmaRecommendationFollowUpFloatingShortcutName,
          }
        : {
            riskSeverities: filters.patients.riskSeverities && filters.patients.riskSeverities.join(','),
            evaluationStates: filters.patients.evaluationStates && filters.patients.evaluationStates.join(','),
            evaluationStateModifiedFrom: filters.patients.evaluationStateModifiedFrom,
            evaluationStateModifiedTo: filters.patients.evaluationStateModifiedTo,
          }),

      page: filters.patientsPaging.page ?? 0,
      size: filters.patientsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
    },
    filters.patientsSorting,
    {
      patientFullName: ['PATIENT_LAST_NAME', 'PATIENT_FIRST_NAME'],
      pharmaRecommendationRiskiness: 'PHARMA_RECOMMENDATION_RISKINESS',
      riskFactorsCount: 'RISK_FACTORS_COUNT',
    }
  );
  // A biochemistry row with a non-numeric value cannot be sent and must not be silently dropped either, so the
  // list is shown empty without asking the backend and the export is blocked until the value is corrected.
  const listBlocked = hasBlockingBiochemistryRow(filters.patients);
  const tableData = useTableData<any, RowType[]>(hospitalizationsUrl, hospitalizationParams, 'content', !listBlocked);
  const data = listBlocked ? [] : tableData.data;
  const isLoading = listBlocked ? false : tableData.isLoading;
  const page = listBlocked ? 0 : tableData.page;
  const countOnPage = listBlocked ? 0 : tableData.countOnPage;
  const totalCount = listBlocked ? 0 : tableData.totalCount;
  const totalPages = listBlocked ? 0 : tableData.totalPages;
  const {handleXlsxExport, warningDialog} = useHospitalizedPatientsExport(filters.patients, config, totalCount);
  const formatters = useFormatters();
  const {t} = formatters;
  const isInitialLoad = useRef(true);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  // Bottom-most filter-inputs row; measured by autoFitFilterRows to default the divider to 2 rows + a peek.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  // Last line of the ATC / active-ingredient slots (the ingredients); a newly added slot never lands below it.
  const medicationSlotsEndRef = useRef<HTMLDivElement | null>(null);
  // Fullscreen results table, the same way the report tables do it.
  const {
    maximized: tableMaximized,
    toggleMaximized: tableToggleMaximized,
    MaximizeWrapper: TableMaximizeWrapper,
  } = useMaximizeWrapper('patient-list-wrapper', {innerStyle: {rowGap: 16}});
  // Fullscreen filter panel (PDM-2842): this wrapper IS the panel's card (16px padding, no bottom padding so the
  // content scrolls to the card edge); the panel itself owns the button and keeps the flag consistent with "open".
  const {
    maximized: filterPanelMaximized,
    setMaximized: setFilterPanelMaximized,
    MaximizeWrapper: FilterPanelMaximizeWrapper,
  } = useMaximizeWrapper('filter-panel-wrapper', {innerStyle: {rowGap: 16, paddingBottom: 0}});
  React.useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    } else if (filtersRef.current && medicationSlotsEndRef.current?.offsetParent) {
      // Only while the medication tab is shown - the panel stays mounted (hidden) on the other tabs, where a chip
      // removed from the chips row would otherwise scroll the filters to a zero-height box.
      // Reveal a newly added slot: scroll just enough to bring the slot lines into view. Not the whole tab panel -
      // the date row below them is usually left just under the fold by the divider and must not pull the view down.
      const scrollBox = filtersRef.current;
      const overflow = getScrollOffsetBottom(scrollBox, medicationSlotsEndRef.current) - scrollBox.clientHeight;
      if (overflow > 0) scrollBox.scrollTo(0, scrollBox.scrollTop + overflow);
    }
  }, [
    filtersRef,
    medicationSlotsEndRef,
    // eslint-disable-next-line
    JSON.stringify([...ATC_SLOT_NAMES, ...ACTIVE_INGREDIENT_SLOT_NAMES].map((name) => filters.patients[name])),
  ]);
  const {renderOption: atcRenderOption, renderTag: atcRenderTag, getNodeIcon: atcGetNodeIcon} = useInactiveAtcRenderProps(t);
  const chipLookups = useHospitalizedPatientsChipLookups(config);
  const {activeIngredientOptions, icdOptions, labMethodOptions, pathogenOptions, resistanceOptions} = chipLookups;
  const tableFiltersForSelectedPane = useMemo(() => hospitalizedPatientsTableFiltersForChips(filters.patients), [filters.patients]);

  if (permissionsLoading) {
    return <LoadingSpinner isVisible text='' />;
  }
  if (!userRoles.HOSPITALIZATIONS_VIEW_ROLE) {
    // TODO: move this out to AppLayoutContent?
    return <Typography variant='h1'>{utils.TranslateUtils.translate('permissions.insuficientrights', intl)}</Typography>;
  }
  const sortByMessageKey = (options) => sortBy(options, [(v) => t(v.messageKey)]);
  const hasFilters = !isEqual(
    normalizeHospitalizedPatientsFiltersForCompare(omitNullsy(filters.patients) as any),
    normalizeHospitalizedPatientsFiltersForCompare(omitNullsy(DEFAULT_PATIENTS_FILTERS) as any)
  );
  const showAlertBell = Boolean(userRoles.ALERT_USE_ROLE);
  const columns = getColumns(formatters, filters.patientsSorting, showAlertBell);
  const atcSlots = ATC_SLOT_NAMES.map((name) => filters.patients[name]);
  const activeIngredientSlots = ACTIVE_INGREDIENT_SLOT_NAMES.map((name) => filters.patients[name]);
  const showAtcSlot = getMultiFilter(atcSlots);
  const showActiveIngredientSlot = getMultiFilter(activeIngredientSlots);
  const showMedicationMatch = countMedicationConditions(filters.patients) >= 2;
  const showBiochemistryRow = showBiochemistryRows(filters.patients);
  // The occurrence selector appears once two distinct methods are set.
  const showBiochemistryMatch = countBiochemistryItems(filters.patients) >= 2;
  const showMicrobiologyRow = showMicrobiologyRows(filters.patients);
  const showMicrobiologyMatch = countMicrobiologyConditions(filters.patients) >= 2;
  const operatorOptions = enumMap.LabValueOperatorEnum ?? [];
  /** Rows close ranks once one is emptied, the same way the medication slots above do. */
  const changeBiochemistryRow = (row: BiochemistryRow, part: 'method' | 'operator' | 'value', newValue: string | null) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...applyBiochemistryRowChange(filters.patients, row, part, newValue),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  const removeBiochemistryRowFilter = (row: BiochemistryRow) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...removeBiochemistryRow(filters.patients, row),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  /** A microbiology row packs the same way; the two multiselects of a row move together. */
  const changeMicrobiologyRow = (row: MicrobiologyRow, part: MicrobiologyPart, newValue: string[] | null) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...applyMicrobiologyRowChange(filters.patients, row, part, newValue),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  const removeMicrobiologyRowFilter = (row: MicrobiologyRow) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...removeMicrobiologyRow(filters.patients, row),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  /** Clearing a slot moves the following ones of its line up, so the box never shows an empty slot above a filled one. */
  const changeMedicationSlot = (name: MedicationSlotName, newValue: string[] | null) => {
    changeFilters({
      patients: {
        ...filters.patients,
        ...packMedicationSlots({...filters.patients, [name]: newValue ?? undefined}),
      },
      patientsPaging: {
        ...filters.patientsPaging,
        page: 0,
      },
    });
  };
  // Other tab (PDM-2749): plain independent fields, so one setter with the paging reset serves them all.
  const changeOther = (patch: Partial<PatientsFilters>) =>
    changeFilters({
      patients: {...filters.patients, ...patch},
      patientsPaging: {...filters.patientsPaging, page: 0},
    });
  const numberRange = (from: number | undefined, to: number | undefined) => (from != null && to != null ? {start: from, end: to} : null);
  // The chip definitions are shared with the alert tooltips, which show a saved filter of this page the same way.
  const selectedFilters: SelectedFilter[] = buildHospitalizedPatientsSelectedFilters(filters.patients, formatters, chipLookups);
  const selectedFiltersCount = countSelectedFilters(selectedFilters, tableFiltersForSelectedPane);
  const filterTabs: LightFilterTabDef[] = [
    {id: 'general', label: t('filter.tabs.general'), iconName: 'hospitalUserIcon', count: selectedFiltersCount.byTab.general},
    {id: 'medication', label: t('filter.tabs.medication'), iconName: 'pillsIcon', count: selectedFiltersCount.byTab.medication},
    {
      id: 'laboratory',
      label: t('patient.detail.healthcare.eventType.laboratory'),
      iconName: 'flaskIcon',
      count: selectedFiltersCount.byTab.laboratory,
    },
    {
      id: 'microbiology',
      label: t('filter.tabs.microbiology'),
      iconName: 'bacteriumIcon',
      count: selectedFiltersCount.byTab.microbiology,
    },
    {id: 'other', label: t('filter.tabs.other'), iconName: 'slidersIcon', count: selectedFiltersCount.byTab.other},
  ];
  const selectedFiltersPane =
    selectedFiltersCount.total > 0 ? (
      <LightSelectedFiltersPane
        selectedFilters={selectedFilters}
        tableFilters={tableFiltersForSelectedPane}
        changeTableFilters={(newTableFilters) => {
          // Removing a biochemistry or medication chip empties its row or slot, so the ones below it move up the
          // same way they do when it is cleared in the form.
          const patients = {...filters.patients, ...newTableFilters};
          changeFilters({
            patients: {
              ...patients,
              ...packBiochemistryRows(patients),
              ...packMedicationSlots(patients),
            },
            patientsPaging: {
              ...filters.patientsPaging,
              page: 0,
            },
          });
        }}
        formatters={formatters}
      />
    ) : null;
  // The inputs of every tab; the panel keeps all of them mounted and shows the active one.
  const tabPanels: Record<HospitalizationFilterTab, React.ReactNode> = {
    general: (
      <>
        <LightTextFilter
          config={{
            name: 'patientFullName',
            ui: {label: 'filter.patient.fullName'},
          }}
          value={filters.patients.patientFullName}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                patientFullName: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'patientIdentificationNumber',
            ui: {label: 'filter.patient.identificationNumber'},
          }}
          value={filters.patients.patientIdentificationNumber}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                patientIdentificationNumber: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'patientSrcId',
            ui: {label: 'filter.patient.sourceId'},
          }}
          value={filters.patients.patientSrcId}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                patientSrcId: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightOrganizationFilter2
          appConfig={config}
          config={{
            name: 'encounterOrganizationIds',
            ui: {
              label: 'filter.patient.admissionDepartment',
            },
          }}
          multi
          value={filters.patients.encounterOrganizationIds}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                encounterOrganizationIds: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightDateRangeFilter
          config={{
            name: 'admissionDate',
            ui: {label: 'filter.patient.admissionDate'},
          }}
          value={
            filters.patients.encounterStartedFrom && filters.patients.encounterStartedTo
              ? {
                  startDate: filters.patients.encounterStartedFrom,
                  endDate: filters.patients.encounterStartedTo,
                  shortcut: filters.patients.encounterFloatingShortcutName,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                encounterStartedFrom: newValue?.startDate,
                encounterStartedTo: newValue?.endDate,
                encounterFloatingShortcutName: newValue?.shortcut,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
          dateOnly
          showShortcuts
        />
        <LightOrganizationFilter2
          appConfig={config}
          config={{
            name: 'lastEncounterSubOrganizationIds',
            ui: {
              label: 'filter.patient.currentDepartment',
            },
          }}
          multi
          value={filters.patients.lastEncounterSubOrganizationIds}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                lastEncounterSubOrganizationIds: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightDateRangeFilter
          config={{
            name: 'transferDate',
            ui: {label: 'filter.patient.transferDate'},
          }}
          value={
            filters.patients.lastEncounterSubStartedFrom && filters.patients.lastEncounterSubStartedTo
              ? {
                  startDate: filters.patients.lastEncounterSubStartedFrom,
                  endDate: filters.patients.lastEncounterSubStartedTo,
                  shortcut: filters.patients.lastEncounterSubFloatingShortcutName,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                lastEncounterSubStartedFrom: newValue?.startDate,
                lastEncounterSubStartedTo: newValue?.endDate,
                lastEncounterSubFloatingShortcutName: newValue?.shortcut,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
          dateOnly
          showShortcuts
        />
        <LightNumberRangeFilter
          config={{
            name: 'riskScoreValue',
            ui: {
              label: 'filter.patient.riskScore',
              ...riskScoreRange,
            },
          }}
          value={
            filters.patients.riskScoreFrom != null && filters.patients.riskScoreTo != null
              ? {
                  start: filters.patients.riskScoreFrom,
                  end: filters.patients.riskScoreTo,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                riskScoreFrom: newValue?.start,
                riskScoreTo: newValue?.end,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightSelectFilter
          config={{
            name: 'riskFactorTypes',
            ui: {label: 'filter.patient.riskFactorTypes'},
          }}
          options={sortByMessageKey(enumMap.RiskFactorTypeEnum ?? [])}
          multi
          value={filters.patients.riskFactorTypes}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                riskFactorTypes: newValue,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightNumberRangeFilter
          config={{
            name: 'riskFactorsCount',
            ui: {
              label: 'filter.patient.riskFactors',
              ...riskFactorsRange,
            },
          }}
          value={
            filters.patients.riskFactorsCountFrom != null && filters.patients.riskFactorsCountTo != null
              ? {
                  start: filters.patients.riskFactorsCountFrom,
                  end: filters.patients.riskFactorsCountTo,
                }
              : null
          }
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                riskFactorsCountFrom: newValue?.start,
                riskFactorsCountTo: newValue?.end,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        {ENABLE_PH_REC ? (
          <>
            <LightSelectFilter
              config={{
                name: 'pharmaRecommendationRiskinesses',
                ui: {label: 'filter.patient.pharmaRecommendationRiskinesses'},
              }}
              options={sortByMessageKey(enumMap.RiskinessHospitalizationFilter ?? [])}
              multi
              value={filters.patients.pharmaRecommendationRiskinesses}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    pharmaRecommendationRiskinesses: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightDateRangeFilter
              config={{
                name: 'pharmaRecommendationDate',
                ui: {label: 'filter.patient.pharmaRecommendationDate'},
              }}
              value={
                filters.patients.pharmaRecommendationDateFrom && filters.patients.pharmaRecommendationDateTo
                  ? {
                      startDate: filters.patients.pharmaRecommendationDateFrom,
                      endDate: filters.patients.pharmaRecommendationDateTo,
                      shortcut: filters.patients.pharmaRecommendationFloatingShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    pharmaRecommendationDateFrom: newValue?.startDate,
                    pharmaRecommendationDateTo: newValue?.endDate,
                    pharmaRecommendationFloatingShortcutName: newValue?.shortcut,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
            <LightDateRangeFilter
              config={{
                name: 'pharmaRecommendationFollowUpDate',
                ui: {label: 'filter.patient.pharmaRecommendationFollowUpDate'},
              }}
              value={
                filters.patients.pharmaRecommendationFollowUpDateFrom && filters.patients.pharmaRecommendationFollowUpDateTo
                  ? {
                      startDate: filters.patients.pharmaRecommendationFollowUpDateFrom,
                      endDate: filters.patients.pharmaRecommendationFollowUpDateTo,
                      shortcut: filters.patients.pharmaRecommendationFollowUpFloatingShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    pharmaRecommendationFollowUpDateFrom: newValue?.startDate,
                    pharmaRecommendationFollowUpDateTo: newValue?.endDate,
                    pharmaRecommendationFollowUpFloatingShortcutName: newValue?.shortcut,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              enableFuture
              showShortcuts
            />
          </>
        ) : (
          <>
            <LightSelectFilter
              config={{
                name: 'riskSeverities',
                ui: {label: 'filter.patient.riskSeverity'},
              }}
              options={sortByMessageKey(enumMap.RiskSeverityEnum ?? [])}
              multi
              value={filters.patients.riskSeverities}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    riskSeverities: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightSelectFilter
              config={{
                name: 'evaluationStates',
                ui: {label: 'filter.patient.checkState'},
              }}
              options={sortByMessageKey(enumMap.EvaluationStateEnum ?? [])}
              multi
              value={filters.patients.evaluationStates}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    evaluationStates: newValue,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightDateRangeFilter
              config={{
                name: 'checkDate',
                ui: {label: 'filter.patient.checkDate'},
              }}
              value={
                filters.patients.evaluationStateModifiedFrom && filters.patients.evaluationStateModifiedTo
                  ? {
                      startDate: filters.patients.evaluationStateModifiedFrom,
                      endDate: filters.patients.evaluationStateModifiedTo,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    evaluationStateModifiedFrom: newValue?.startDate,
                    evaluationStateModifiedTo: newValue?.endDate,
                  },
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
          </>
        )}
        <LightSelectFilter
          config={{
            name: 'hospitalizationStates',
            ui: {label: 'filter.patient.hospitalizations'},
          }}
          options={sortByMessageKey(enumMap.HospitalizationStateEnum ?? [])}
          multi
          value={filters.patients.hospitalizationStates}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                hospitalizationStates: newValue,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
        <LightTextFilter
          config={{
            name: 'commentSearchString',
            ui: {label: 'filter.patient.commentSearchString'},
          }}
          value={filters.patients.commentSearchString}
          onChange={(newValue) => {
            changeFilters({
              patients: {
                ...filters.patients,
                commentSearchString: newValue ?? undefined,
              },
              patientsPaging: {
                ...filters.patientsPaging,
                page: 0,
              },
            });
          }}
        />
      </>
    ),
    medication: (
      <div css={muiStyles.tabPanel}>
        <div css={muiStyles.medicationsFilterLine}>
          {ATC_SLOT_NAMES.map(
            (name, index) =>
              showAtcSlot[index] && (
                <React.Fragment key={name}>
                  {index > 0 && <div css={muiStyles.medicationsFilterJoiner}>&</div>}
                  <LightMedicationAtcFilter2
                    css={muiStyles.medicationsFilter}
                    appConfig={config}
                    config={{
                      name,
                      ui: {label: ATC_SLOT_LABELS[index]},
                    }}
                    multi
                    value={atcSlots[index] ?? null}
                    onChange={(newValue) => changeMedicationSlot(name, newValue)}
                    getNodeIcon={(params) => atcGetNodeIcon(params.node)}
                    renderOption={atcRenderOption}
                    renderTag={atcRenderTag}
                  />
                </React.Fragment>
              )
          )}
        </div>
        <div ref={medicationSlotsEndRef} css={muiStyles.medicationsFilterLine}>
          {ACTIVE_INGREDIENT_SLOT_NAMES.map(
            (name, index) =>
              showActiveIngredientSlot[index] && (
                <React.Fragment key={name}>
                  {index > 0 && <div css={muiStyles.medicationsFilterJoiner}>&</div>}
                  <LightSelectFilterVirtualized
                    css={muiStyles.medicationsFilter}
                    config={{
                      name,
                      ui: {label: ACTIVE_INGREDIENT_SLOT_LABELS[index]},
                    }}
                    options={activeIngredientOptions}
                    multi
                    value={activeIngredientSlots[index] ?? null}
                    onChange={(newValue) => changeMedicationSlot(name, newValue)}
                  />
                </React.Fragment>
              )
          )}
        </div>
        <div css={muiStyles.medicationsFilterLine}>
          <LightDateRangeFilter
            config={{
              name: 'atcHierarchyDate',
              ui: {label: 'filter.patient.atcHierarchyDate'},
            }}
            value={
              filters.patients.atcHierarchyFrom && filters.patients.atcHierarchyTo
                ? {
                    startDate: filters.patients.atcHierarchyFrom,
                    endDate: filters.patients.atcHierarchyTo,
                    shortcut: filters.patients.atcHierarchyFloatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                patients: {
                  ...filters.patients,
                  atcHierarchyFrom: newValue?.startDate,
                  atcHierarchyTo: newValue?.endDate,
                  atcHierarchyFloatingShortcutName: newValue?.shortcut,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            dateOnly
            showShortcuts
            isMedications
          />
          {showMedicationMatch && (
            <LightSelectFilter
              config={{
                name: 'atcHierarchyMatch',
                ui: {label: 'filter.patient.atcHierarchyMatch'},
              }}
              options={sortByMessageKey(enumMap.AtcHierarchyFilterMatchEnum ?? [])}
              maxWidth={MATCH_FILTER_WIDTH}
              style={{width: MATCH_FILTER_WIDTH}}
              value={filters.patients.atcHierarchyMatch}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    atcHierarchyMatch: newValue,
                  },
                });
              }}
              isClearable={false}
            />
          )}
        </div>
      </div>
    ),
    laboratory: (
      <div css={muiStyles.tabPanel}>
        {BIOCHEMISTRY_ROWS.map(
          (row, index) =>
            showBiochemistryRow[index] && (
              <div css={muiStyles.conditionRowBox} key={row.method}>
                <LightSelectFilterVirtualized
                  css={muiStyles.medicationsFilter}
                  config={{name: row.method, ui: {label: row.label}}}
                  options={labMethodOptions}
                  value={filters.patients[row.method] ?? null}
                  onChange={(newValue) => changeBiochemistryRow(row, 'method', newValue)}
                />
                <LightSelectFilter
                  css={muiStyles.medicationsFilter}
                  // Same width as the value input next to it. The wrapper takes the width from these props, a `css`
                  // override does not reach it. The inline style makes the width definite: with only max-width the
                  // field would still be laid out at 128px, but the box around the row sizes itself to its
                  // content and would count the select's narrower intrinsic width - and wrap the value field.
                  maxWidth={BIOCHEMISTRY_VALUE_WIDTH}
                  style={{width: BIOCHEMISTRY_VALUE_WIDTH}}
                  config={{name: row.operator, ui: {label: row.operatorLabel}}}
                  options={operatorOptions}
                  value={filters.patients[row.operator] ?? null}
                  onChange={(newValue) => changeBiochemistryRow(row, 'operator', newValue)}
                />
                {/* The cross stays glued to the value field, so a wrapped row never drops it on a line of its own. */}
                <span css={muiStyles.biochemistryValueWithRemove}>
                  <LightTextFilter
                    config={{name: row.value, ui: {label: row.valueLabel}}}
                    value={filters.patients[row.value]}
                    onChange={(newValue) => changeBiochemistryRow(row, 'value', newValue)}
                    inputWidth={`${BIOCHEMISTRY_VALUE_WIDTH}px`}
                    iconName='numbersIcon'
                  />
                  {/* A row is three fields, so it needs one visible control that removes all of them. The cross is
                      always laid out and only shown once the row is started, so every row's grey box keeps the
                      same width whether or not it can be removed. */}
                  <DlMuiDynamicIcon
                    iconName='haidiCloseIcon2'
                    title={t('filter.patient.biochemistryRemoveRow')}
                    useFixedViewBox={12}
                    noButtonStyles
                    css={muiStyles.biochemistryRemoveRow}
                    svgIconInputCss={muiStyles.biochemistryRemoveRowIcon}
                    onClick={() => removeBiochemistryRowFilter(row)}
                    style={isBiochemistryRowStarted(filters.patients, row) ? undefined : {visibility: 'hidden'}}
                    tabIndex={isBiochemistryRowStarted(filters.patients, row) ? undefined : -1}
                    aria-hidden={!isBiochemistryRowStarted(filters.patients, row)}
                  />
                </span>
              </div>
            )
        )}
        <div css={muiStyles.medicationsFilterLine}>
          <LightDateRangeFilter
            config={{
              name: 'biochemistryDate',
              ui: {label: 'filter.patient.biochemistryDate'},
            }}
            value={
              filters.patients.biochemistryFrom && filters.patients.biochemistryTo
                ? {
                    startDate: filters.patients.biochemistryFrom,
                    endDate: filters.patients.biochemistryTo,
                    shortcut: filters.patients.biochemistryFloatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                patients: {
                  ...filters.patients,
                  biochemistryFrom: newValue?.startDate,
                  biochemistryTo: newValue?.endDate,
                  biochemistryFloatingShortcutName: newValue?.shortcut,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            dateOnly
            showShortcuts
          />
          {showBiochemistryMatch && (
            <LightSelectFilter
              config={{
                name: 'biochemistryMatch',
                ui: {label: 'filter.patient.biochemistryMatch'},
              }}
              options={sortByMessageKey(enumMap.AtcHierarchyFilterMatchEnum ?? [])}
              maxWidth={MATCH_FILTER_WIDTH}
              style={{width: MATCH_FILTER_WIDTH}}
              value={filters.patients.biochemistryMatch}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    biochemistryMatch: newValue,
                  },
                });
              }}
              isClearable={false}
            />
          )}
        </div>
      </div>
    ),
    microbiology: (
      <div css={muiStyles.tabPanel}>
        {MICROBIOLOGY_ROWS.map(
          (row, index) =>
            showMicrobiologyRow[index] && (
              <div css={muiStyles.conditionRowBox} key={row.pathogens}>
                <LightSelectFilterVirtualized
                  css={muiStyles.medicationsFilter}
                  config={{name: row.pathogens, ui: {label: row.pathogensLabel}}}
                  options={pathogenOptions}
                  multi
                  value={filters.patients[row.pathogens] ?? null}
                  onChange={(newValue) => changeMicrobiologyRow(row, 'pathogens', newValue)}
                />
                {/* The cross stays glued to the resistance field, like the biochemistry rows keep it by the value. */}
                <span css={muiStyles.biochemistryValueWithRemove}>
                  <LightSelectFilterVirtualized
                    css={muiStyles.medicationsFilter}
                    config={{name: row.resistances, ui: {label: row.resistancesLabel}}}
                    options={resistanceOptions}
                    multi
                    value={filters.patients[row.resistances] ?? null}
                    onChange={(newValue) => changeMicrobiologyRow(row, 'resistances', newValue)}
                  />
                  <DlMuiDynamicIcon
                    iconName='haidiCloseIcon2'
                    title={t('filter.patient.biochemistryRemoveRow')}
                    useFixedViewBox={12}
                    noButtonStyles
                    css={muiStyles.microbiologyRemoveRow}
                    svgIconInputCss={muiStyles.biochemistryRemoveRowIcon}
                    onClick={() => removeMicrobiologyRowFilter(row)}
                    style={isMicrobiologyRowStarted(filters.patients, row) ? undefined : {visibility: 'hidden'}}
                    tabIndex={isMicrobiologyRowStarted(filters.patients, row) ? undefined : -1}
                    aria-hidden={!isMicrobiologyRowStarted(filters.patients, row)}
                  />
                </span>
              </div>
            )
        )}
        <div css={muiStyles.medicationsFilterLine}>
          <LightDateRangeFilter
            config={{
              name: 'microbiologyDate',
              ui: {label: 'filter.patient.microbiologyDate'},
            }}
            value={
              filters.patients.microbiologyFrom && filters.patients.microbiologyTo
                ? {
                    startDate: filters.patients.microbiologyFrom,
                    endDate: filters.patients.microbiologyTo,
                    shortcut: filters.patients.microbiologyFloatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                patients: {
                  ...filters.patients,
                  microbiologyFrom: newValue?.startDate,
                  microbiologyTo: newValue?.endDate,
                  microbiologyFloatingShortcutName: newValue?.shortcut,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            dateOnly
            showShortcuts
          />
          {showMicrobiologyMatch && (
            <LightSelectFilter
              config={{
                name: 'microbiologyMatch',
                ui: {label: 'filter.patient.microbiologyMatch'},
              }}
              options={sortByMessageKey(enumMap.AtcHierarchyFilterMatchEnum ?? [])}
              maxWidth={MATCH_FILTER_WIDTH}
              style={{width: MATCH_FILTER_WIDTH}}
              value={filters.patients.microbiologyMatch}
              onChange={(newValue) => {
                changeFilters({
                  patients: {
                    ...filters.patients,
                    microbiologyMatch: newValue,
                  },
                });
              }}
              isClearable={false}
            />
          )}
        </div>
      </div>
    ),
    // Other (PDM-2749): independent conditions, one line that wraps on a narrow screen. The diagnosis type
    // (admission / ongoing) joins the ICD field once the import carries it.
    other: (
      <div css={muiStyles.tabPanel}>
        <div css={muiStyles.medicationsFilterLine}>
          <LightSelectFilterVirtualized
            config={{name: 'icdCodes', ui: {label: 'filter.patient.icdCodes'}}}
            options={icdOptions}
            multi
            value={filters.patients.icdCodes ?? null}
            onChange={(newValue) => changeOther({icdCodes: newValue ?? undefined})}
          />
          <LightNumberRangeFilter
            config={{name: 'ageAtEnd', ui: {label: 'filter.patient.age', ...AGE_RANGE}}}
            value={numberRange(filters.patients.ageFrom, filters.patients.ageTo)}
            onChange={(newValue) => changeOther({ageFrom: newValue?.start, ageTo: newValue?.end})}
          />
          <LightNumberRangeFilter
            config={{name: 'weight', ui: {label: 'filter.patient.weight', ...WEIGHT_RANGE}}}
            value={numberRange(filters.patients.weightFrom, filters.patients.weightTo)}
            onChange={(newValue) => changeOther({weightFrom: newValue?.start, weightTo: newValue?.end})}
          />
          <LightNumberRangeFilter
            config={{name: 'bmi', ui: {label: 'filter.patient.bmi', ...BMI_RANGE}}}
            value={numberRange(filters.patients.bmiFrom, filters.patients.bmiTo)}
            onChange={(newValue) => changeOther({bmiFrom: newValue?.start, bmiTo: newValue?.end})}
          />
          <LightSelectFilter
            config={{name: 'deathDuringHospitalizations', ui: {label: 'filter.patient.deathDuringHospitalization'}}}
            // Enum order, not alphabetical: "yes", "yes, in text only" read as a scale. The "-" (no record)
            // goes first like in the other filters, where it lands there by the alphabetical sort.
            options={sortBy(enumMap.DeathDuringHospitalizationEnum ?? [], (v) => v.name !== 'NO_RECORD')}
            multi
            value={filters.patients.deathDuringHospitalizations}
            onChange={(newValue) => changeOther({deathDuringHospitalizations: newValue ?? undefined})}
          />
        </div>
      </div>
    ),
  };
  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={utils.TranslateUtils.translate(I18N.PAGE_TITLE, intl)} />
      <LightSplitLayout
        initSizes={PATIENTS_LAYOUT_SPLIT_CONFIG[0].sizes}
        minSizes={PATIENTS_LAYOUT_SPLIT_CONFIG[0].minSize}
        storageId={PATIENTS_LAYOUT_SPLIT_CONFIG[0].layoutId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.patientsOpen, false]}
        hidden={[false, false]}
        autoFitFilterRows={{contentRef: filterInputsRef, enabled: filters.patientsOpen}}
      >
        <FilterPanelMaximizeWrapper>
          <LightFilterPanel
            open={filters.patientsOpen}
            toggleOpen={() =>
              changeFilters({
                patientsOpen: !filters.patientsOpen,
              })
            }
            hasActiveFilter={hasFilters}
            scrollAreaRef={filtersRef}
            filterInputsRef={filterInputsRef}
            filterInputsMinWidth={BIOCHEMISTRY_ROW_WIDTH}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: filters.patients,
              setFilter: (newFilter) => {
                changeFilters({
                  patients: newFilter,
                });
              },
              config,
              normalizeForCompare: normalizeHospitalizedPatientsFiltersForCompare,
              // the row bells show the alerts' weights and order, so a changed alert reloads the list
              onAlertsChanged: tableData.refetch,
            }}
            selectedFilters={selectedFiltersPane}
            selectedFiltersCount={selectedFiltersCount.total}
            maximize={{maximized: filterPanelMaximized, setMaximized: setFilterPanelMaximized}}
            tabs={{
              tabs: filterTabs,
              activeTab: filters.patientsTab,
              onChange: (tabId, open) =>
                changeFilters({patientsTab: tabId as HospitalizationFilterTab, ...(open ? {patientsOpen: true} : {})}),
              panels: tabPanels,
            }}
          />
        </FilterPanelMaximizeWrapper>
        <TableMaximizeWrapper>
          <IconHeaderWithSearch iconName={'listPlusIcon'} label={'patient.list.header.title'} childrenWrapperStyle={{gap: 6}}>
            <LightSelectedFilters
              align='flex-start'
              marginLeft='8px'
              hasFilters={hasFilters}
              handleResetFilters={() => {
                changeFilters({
                  patients: DEFAULT_PATIENTS_FILTERS,
                  patientsPaging: {
                    ...filters.patientsPaging,
                    page: 0,
                  },
                });
              }}
              hasSorting={!isEqual(filters.patientsSorting, DEFAULT_PATIENTS_SORTING)}
              handleResetSorting={() => {
                changeFilters({
                  patientsSorting: DEFAULT_PATIENTS_SORTING,
                });
              }}
              hasXlsxExport
              handleXlsxExport={handleXlsxExport}
              xlsxExportDisabled={listBlocked}
              xlsxExportDisabledTooltip='filter.patient.biochemistryValueInvalid'
            />
            <MaximizeButton maximized={tableMaximized} toggleMaximized={tableToggleMaximized} />
            {warningDialog}
          </IconHeaderWithSearch>
          <MaterialTableWrapper
            variant='big'
            options={{
              ...getDefaultMTOptions(countOnPage ?? DEFAULT_PAGE_SIZE),
              padding: 'dense',
            }}
            cellLineHeight='2.2'
            useMaxHeight
            columns={columns}
            getRowClass={showAlertBell ? withAlertRowClass<RowType>() : undefined}
            data={data ?? []}
            isLoading={isLoading}
            totalCount={totalCount}
            page={page}
            totalPages={totalPages}
            onChangePage={(page, pageSize) =>
              changeFilters({
                patientsPaging: {
                  ...filters.patientsPaging,
                  page,
                  pageSize,
                },
              })
            }
            onChangeRowsPerPage={(pageSize) =>
              changeFilters({
                patientsPaging: {
                  ...filters.patientsPaging,
                  pageSize,
                  page: clampPageNumber(filters.patientsPaging.page, pageSize, totalCount),
                },
              })
            }
            controlledPageSize={filters.patientsPaging.pageSize ?? DEFAULT_PAGE_SIZE}
            onOrderChange={(colIndex, direction) => {
              const columnId = columns[colIndex].field as string;
              changeFilters({
                patientsSorting: {
                  field: columnId,
                  direction,
                },
                patientsPaging: {
                  ...filters.patientsPaging,
                  page: 0,
                },
              });
            }}
            controlledSort={(() => {
              return filters.patientsSorting;
            })()}
            scrollStorageId='/HospitalizedPatientsLayout.scrollXY'
          />
        </TableMaximizeWrapper>
      </LightSplitLayout>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  /** Content of a grouped tab (medication, biochemistry): one line per row, stacked; takes the whole inputs row. */
  tabPanel: css`
    flex-basis: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    row-gap: 4px;
  `,
  /**
   * One biochemistry condition (method + operator + value + its remove cross) in a content-hugging grey box, so the
   * three fields read as one condition (design PDM-2750). Same box look as the former medication box.
   */
  conditionRowBox: css`
    padding: 1px 8px 4px 8px;
    border-radius: 4px;
    background: var(--dlThemeGray-50);
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 4px;
    max-width: 100%;
  `,
  /** One line of the medication box: ATC slots, active ingredient slots, or the shared date + occurrence. */
  medicationsFilterLine: css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    column-gap: 8px;
    /* Wrapping is expected on a narrow screen; without this the wrapped fields would touch. */
    row-gap: 4px;
  `,
  medicationsFilterJoiner: css`
    font-family: var(--dlFontFamilyNormal);
    font-size: 14px;
    color: #121619;
  `,
  /** Holds the value field and its remove cross together, so the cross never wraps away from its row. */
  biochemistryValueWithRemove: css`
    display: inline-flex;
    flex-wrap: nowrap;
    align-items: center;
    column-gap: 2px;
  `,
  /** Removes the whole condition. Flat and small, like the crosses on the chips below. */
  biochemistryRemoveRow: css`
    padding: 2px;
    color: var(--dlThemeGray-500);
    &:hover {
      color: var(--dlTheme-700);
    }
  `,
  biochemistryRemoveRowIcon: css`
    font-size: 14px;
  `,
  /** The microbiology rows' cross: the same look, pushed out to where the biochemistry rows have theirs. */
  microbiologyRemoveRow: css`
    padding: 2px;
    margin-left: ${CONDITION_ROW_REMOVE_GAP}px;
    color: var(--dlThemeGray-500);
    &:hover {
      color: var(--dlTheme-700);
    }
  `,
  medicationsFilter: css`
    background: #fff;
    border-radius: 4px;
  `,
});
